import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./app.tsx";
import "./index.css";
import { enableMockServer } from "./lib/utils.ts";
import { ErrorBoundary } from "react-error-boundary";
import { logError } from "./lib/logging.ts";
import * as Sentry from "@sentry/react";
import { FatalErrorView } from "./fatal-error-view.tsx";
import { routerBase } from "./../config.json";

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        networkDetailAllowUrls: [/.*\.emea\.group\.grundfos\.com/],
      }),
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [
          [400, 499],
          [500, 599],
        ],
      }),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Session Replay
    replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

async function enableMocking() {
  // Enabling mocking for local dev and netlify deploy preview
  if (!enableMockServer()) {
    return;
  }

  const { worker } = await import("./mocks/browser.ts");

  await worker.start({
    serviceWorker: {
      url: `${routerBase || "/"}mockServiceWorker.js`,
    },
  });
}

enableMocking().then(() =>
  createRoot(document.getElementById("root")!).render(
    <ErrorBoundary
      fallbackRender={FatalErrorView}
      onError={(e) => logError("client", e)}
    >
      <StrictMode>
        <App />
      </StrictMode>
    </ErrorBoundary>
  )
);
